import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Button, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import DeleteUserGroupDialog from "./DeleteUserGroupDialog";
import { UserGroupDetailsContextProvider } from "./UserGroupDetailsContext";
import UserGroupDetailsPageContainer from "./UserGroupDetailsPageContainer";
import {
  getInitialUserGroupDetailsState,
  getUpdateClientUserGroupRequest,
  isUserGroupDetailsStateDirty,
  updateWithStoredUserGroupAction,
} from "./userGroupDetailsState";

const updateClientUserGroup = withErrorHandling(adminApi.updateClientUserGroup);

const UserGroupDetailsPage = () => {
  const { id } = useParams();
  const { hasAnyPermission } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const navigate = useNavigate();

  const [state, setState] = useState(getInitialUserGroupDetailsState);
  const [saving, setSaving] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const getUserGroupDetails = useCallback(() => adminApi.getClientUserGroupDetails(id ?? ""), [id]);

  const [userGroup, fetchGroupError, { setData: setUserGroup }] = useFetch(getUserGroupDetails, {
    onSetData: (data) => setState(updateWithStoredUserGroupAction(data)),
  });

  const [availableUsers, fetchUsersError] = useFetch(adminApi.getAvailableClientUsers);

  if (!id) {
    return <Navigate to=".." />;
  }

  const fetchError = fetchGroupError || fetchUsersError;
  if (fetchError) {
    logError(fetchError, "[UserGroupDetailsPage] getUserGroupDetails");
    return <DataLoadingFailed />;
  }

  if (userGroup === undefined || availableUsers === undefined) {
    return <InlineLoader />;
  }

  const handleCancel = () => {
    setState(updateWithStoredUserGroupAction(userGroup));
  };

  const handleSave = async () => {
    setSaving(true);
    const [updatedGroup, error] = await updateClientUserGroup(id, getUpdateClientUserGroupRequest(state, userGroup));
    setSaving(false);

    if (error) {
      logError(error, "[UserGroupDetailsPage] updateClientUserGroup");
      sendNotificationError(error.message);
      return;
    }

    setUserGroup(updatedGroup);
    setState(updateWithStoredUserGroupAction(updatedGroup));
    sendNotification("User group updated successfully");
  };

  const hasPermissionToManage = hasAnyPermission(["ManageOrganizationUsers"]);

  const isDirty = isUserGroupDetailsStateDirty(state, userGroup);

  return (
    <>
      <GeneralPageHeader
        TitleComponent={
          <Stack spacing={0.5}>
            <Typography variant="h6">{userGroup.name}</Typography>
            <Typography variant="caption" color="textSecondary">
              User Group
            </Typography>
          </Stack>
        }
        showDefaultBackButtonTo=".."
      >
        {hasPermissionToManage && (
          <>
            <Button variant="text" color="secondary" disabled={!isDirty || saving} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!state.validationResult.isValid || !isDirty}
              onClick={handleSave}
              loading={saving}
            >
              Save
            </Button>
            <ActionsMenuButton
              items={[
                { label: "Delete", icon: <DeleteIcon color="error" />, onClick: () => setIsDeleteDialogOpen(true) },
              ]}
            />
          </>
        )}
      </GeneralPageHeader>
      <UserGroupDetailsContextProvider
        state={state}
        setState={setState}
        hasPermissionToManage={hasPermissionToManage}
        availableMembers={availableUsers}
      >
        <UserGroupDetailsPageContainer />
      </UserGroupDetailsContextProvider>
      {isDeleteDialogOpen && (
        <DeleteUserGroupDialog
          userGroup={userGroup}
          onClose={() => setIsDeleteDialogOpen(false)}
          onDeleted={() => navigate("..")}
        />
      )}
    </>
  );
};

export default UserGroupDetailsPage;
